import * as React from "react";

import {
  panWindow,
  spanOf,
  type TimeWindow,
  type WindowUpdate,
  zoomWindow,
} from "./window";

/**
 * A pointer that moves less than this many pixels between down and up is a
 * tap (or a click), not a drag. Shared by the tracks and the overview strip.
 */
export const DRAG_THRESHOLD_PX = 4;
/** Scales wheel travel to a zoom factor: `exp(deltaY · ZOOM_PER_PX)`. */
export const ZOOM_PER_PX = 0.004;
/** The pixel-mode `deltaY` one mouse-wheel notch delivers; a keyboard zoom step zooms by exactly that much travel. */
export const WHEEL_NOTCH_PX = 100;
/** A line-mode wheel event (a mouse wheel on Firefox) is treated as this many pixels. */
const LINE_PX = 16;

export interface GestureOptions {
  timeWindow: TimeWindow;
  setTimeWindow: (update: WindowUpdate) => void;
  /** A press-and-release with no movement on empty track space (not on a `[data-clip]`). */
  onTapEmpty?: () => void;
}

export interface GestureHandlers {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
}

interface Drag {
  x: number;
  /** The window when the press began; the pan is measured from it. */
  window: TimeWindow;
  pointerId: number;
  /** Set once the pointer has moved past the tap threshold. */
  moved: boolean;
  /** The press began on a clip, so releasing it is that clip's click, not a tap on empty space. */
  onClip: boolean;
}

/**
 * Pointer and wheel gestures on the tracks, turned into window actions:
 * drag to pan; Ctrl/⌘ + wheel (or pinch) zooms around the cursor; Shift +
 * wheel or a horizontal swipe pans. Pixels appear only here, read from the
 * element at event time and never stored. The in-progress drag is the one
 * piece of mutable state, held in a ref because it is gesture bookkeeping
 * that no render reads.
 *
 * Pointer capture is taken only once a press becomes a drag: capturing on
 * the press would steal the click from a clip it began on. Once captured,
 * the browser delivers the trailing `click` to the capturing element, not
 * the clip, so a drag that ends over a clip does not select it.
 */
export function useTimelineGestures(
  ref: React.RefObject<HTMLElement>,
  { timeWindow, setTimeWindow, onTapEmpty }: GestureOptions,
): { dragging: boolean; handlers: GestureHandlers } {
  const [dragging, setDragging] = React.useState(false);
  const drag = React.useRef<Drag | null>(null);

  // Wheel must be non-passive to preventDefault, which React's synthetic
  // onWheel cannot be. The updater form keeps the listener free of state.
  React.useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }
    const onWheel = (event: WheelEvent) => {
      const horizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY);
      const zoomIntent = event.ctrlKey || event.metaKey;
      if (!zoomIntent && !horizontal && !event.shiftKey) {
        return; // plain scrolling belongs to the page
      }
      event.preventDefault();
      const rect = element.getBoundingClientRect();
      const unit = event.deltaMode === WheelEvent.DOM_DELTA_LINE ? LINE_PX : 1;
      if (zoomIntent) {
        const fraction = (event.clientX - rect.left) / rect.width;
        const factor = Math.exp(event.deltaY * unit * ZOOM_PER_PX);
        setTimeWindow((window) =>
          zoomWindow(window, factor, window.start + fraction * spanOf(window)),
        );
      } else {
        const delta = (horizontal ? event.deltaX : event.deltaY) * unit;
        setTimeWindow((window) =>
          panWindow(window, (delta / rect.width) * spanOf(window)),
        );
      }
    };
    element.addEventListener("wheel", onWheel, { passive: false });
    return () => element.removeEventListener("wheel", onWheel);
  }, [ref, setTimeWindow]);

  // One pointer at a time: a second finger's press is ignored until the
  // first lifts, and only the pointer that started the drag moves or ends it.
  const onPointerDown = (event: React.PointerEvent<HTMLElement>) => {
    if (event.button !== 0 || drag.current !== null) {
      return;
    }
    const target = event.target as Element;
    drag.current = {
      x: event.clientX,
      window: timeWindow,
      pointerId: event.pointerId,
      moved: false,
      onClip: target.closest("[data-clip]") !== null,
    };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) {
      return;
    }
    if (!current.moved) {
      // The press is captured only once it moves past the threshold, so a
      // release outside the plot before then is never seen here. A move with
      // the primary button no longer held means it was: drop the press.
      if ((event.buttons & 1) === 0) {
        drag.current = null;
        return;
      }
      if (Math.abs(event.clientX - current.x) < DRAG_THRESHOLD_PX) {
        return;
      }
      current.moved = true;
      event.currentTarget.setPointerCapture(current.pointerId);
      setDragging(true);
    }
    const rectWidth = event.currentTarget.getBoundingClientRect().width || 1;
    const delta =
      ((event.clientX - current.x) / rectWidth) * spanOf(current.window);
    setTimeWindow(panWindow(current.window, -delta));
  };

  const endDrag = (event: React.PointerEvent<HTMLElement>) => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) {
      return;
    }
    if (event.currentTarget.hasPointerCapture(current.pointerId)) {
      event.currentTarget.releasePointerCapture(current.pointerId);
    }
    drag.current = null;
    setDragging(false);
    if (!current.moved && !current.onClip && event.type === "pointerup") {
      onTapEmpty?.();
    }
  };

  return {
    dragging,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    },
  };
}
